import { Component, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { ContactTypeEnums } from 'src/app/enum/contact-type-enums';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ContactModel } from 'src/app/models/archive/contact.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ContactTableModel } from 'src/app/models/tables/archive/contact-table.model';
import { ContactService } from 'src/app/services/archive/contact.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { ValidateEmailService } from 'src/app/services/global-services/validate-email.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-contact-reference-modal',
  templateUrl: './contact-reference-modal.component.html',
  styleUrls: ['./contact-reference-modal.component.scss'],
  standalone: false
})

export class ContactReferenceModalComponent implements OnInit {

  ascendant: boolean = true;
  contactContract: ContactModel = new ContactModel();
  entityId: number = 0;
  columnSort: string | null = null;
  typeContact: number = ContactTypeEnums.client;
  detailData: PagedResponseModel<ContactTableModel> = new PagedResponseModel<ContactTableModel>();
  formContact: FormGroup = new FormGroup({});
  header: ColumnResponseModel[] = [];
  input$: BehaviorSubject<any>;
  isContact: boolean = true;
  limit: number = 10;
  output$: Subject<any>;
  page: number = 1;
  suscription: Subscription;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    },
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      action: 'delete'
    }
  ];

  constructor(public config: DynamicDialogConfig,
    private confirmDialogService: ConfirmDialogService,
    private formBuilder: FormBuilder,
    private contactService: ContactService,
    private toastHandlerService: ToastHandlerService,
    private validateEmailService: ValidateEmailService,
    @Optional() private ref: DynamicDialogRef,) {
    this.validateForm(this.contactContract);
  }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.suscription = input$?.subscribe((inputs: any) => {
      this.entityId = inputs.entityId;
      this.isContact = inputs.isContact;
      this.typeContact = inputs.typeContact;
    });
    await Promise.all([
      this.getColumns(),
      this.getAllContactsWithPagination()
    ]);
  }

  ngOnDestroy() {
    this.suscription?.unsubscribe();
  }

  close() {
    this.ref.close();
  }

  async delete(contactId: number) {
    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar el contacto?')) {
      try {
        await firstValueFrom(this.contactService.deleteContact(contactId));
        this.newContact();
        await this.getAllContactsWithPagination();
        this.toastHandlerService.showToast(
          'success',
          'El contacto fue eliminado exitosamente.'
        );
      } catch (error) {
        console.error('Error al eliminar contacto.', error);
      }
    }
  }

  async exportGeneralConsultation() {
    try {
      const response = await firstValueFrom(
        this.contactService.exportGeneralConsultation(
          this.entityId, this.typeContact, this.isContact, [], this.columnSort, this.ascendant
        )
      );
      const blob = response;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ContactosReferencias.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al exportar contactos:', error);
    }
  }

  async getAllContactsWithPagination() {
    try {
      this.detailData = await firstValueFrom(
        this.contactService.getAllContactsWithPagination(
          this.entityId, this.typeContact, this.isContact, this.page, this.limit, this.columnSort, this.ascendant, []
        )
      );
    } catch (error) {
      console.error('Error al buscar los contactos.', error);
    }
  }

  async getContactById(contactId: number) {
    try {
      this.contactContract = await firstValueFrom(this.contactService.getContactById(contactId));
      this.validateForm(this.contactContract);
    } catch (error) {
      console.error('Error al editar contacto.', error);
    }
  }

  async getColumns() {
    try {
      if (this.header.length === 0) {
        this.header = await firstValueFrom(this.contactService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta de contactos.', error);
    }
  }

  async newContact() {
    this.contactContract = new ContactModel();
    this.assignEntityIdByType(this.contactContract)
    this.validateForm(this.contactContract);
  }

  async onAction(event: { action: string, item?: ContactTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      await this.getContactById(event.item.id);
    }
    if (event.action === 'delete' && event.item) {
      await this.delete(event.item.id);
    }
  }

  async onExport() {
    await this.exportGeneralConsultation();
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    await this.getAllContactsWithPagination();
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  assignEntityIdByType(contact: ContactModel) {
    switch (this.typeContact) {
      case ContactTypeEnums.client:
        contact.clientId = this.entityId;
        break;
      case ContactTypeEnums.constructionSite:
        contact.constructionSiteId = this.entityId;
        break;
      case ContactTypeEnums.provider:
        contact.providerId = this.entityId;
        break;
    }
  }

  async send() {
    this.validateFormControls();
    if (!this.formContact.valid) {
      this.showValidationMessages();
      return;
    }

    const modelContact = this.formContact.value as ContactModel;
    modelContact.typeContact = this.typeContact;
    modelContact.isContact = this.isContact;
    this.assignEntityIdByType(modelContact);
    if (modelContact.position?.trim() === "") modelContact.position = null;
    if (!this.validateEmailService.validateEmails(this.formContact.get("email")?.value)) return;
    if (this.contactContract.contactId === 0) {
      try {
        this.contactContract = await firstValueFrom(this.contactService.createContact(modelContact));
        await this.getAllContactsWithPagination();
        this.newContact();
        this.toastHandlerService.showToast(
          'success',
          'El contacto fue creado exitosamente.'
        );
      } catch (error) {
        console.error('Error al crear el contacto.', error);
      }
    } else {
      this.contactContract.name = modelContact.name;
      this.contactContract.position = modelContact.position;
      this.contactContract.phone = modelContact.phone.toString();
      this.contactContract.phone2 = modelContact.phone2.toString();
      this.contactContract.email = modelContact.email;
      try {
        this.contactContract = await firstValueFrom(this.contactService.updateContact(this.contactContract));
        await this.getAllContactsWithPagination();
        this.newContact();
        this.toastHandlerService.showToast(
          'success',
          'El contacto fue modificado exitosamente.'
        );
      } catch (error) {
        console.error('Error al modificar el contacto.', error);
      }
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      name: {
        required: 'El campo Nombre es obligatorio.'
      },
      phone: {
        required: 'El campo Teléfono 1 es obligatorio.',
        minlength: 'El campo Teléfono 1 debe tener al menos 7 caracteres.',
        maxlength: 'El campo Teléfono 1 no puede tener más de 50 caracteres.'
      },
      phone2: {
        required: 'El campo Teléfono 2 es obligatorio.',
        minlength: 'El campo Teléfono 2 debe tener al menos 7 caracteres.',
        maxlength: 'El campo Teléfono 2 no puede tener más de 50 caracteres.'
      },
    };
    Object.keys(controlMessages).forEach(control => {
      const formControl = this.formContact.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm(contact: ContactModel) {
    this.formContact = this.formBuilder.group({
      name: [contact.name, [Validators.required]],
      position: [contact.position],
      phone: [contact.phone, [Validators.required, Validators.minLength(7), Validators.maxLength(50)]],
      phone2: [contact.phone2, [Validators.minLength(7), Validators.maxLength(50)]],
      email: [contact.email]
    });
  }

  validateFormControls() {
    Object.keys(this.formContact.controls).forEach((key) => {
      const control = this.formContact.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
